import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Tabs, Tab, Button, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { LintTriage } from './LintTriage';
import { TestFailures } from './TestFailures';
import { PlaywrightTests } from './PlaywrightTests';

const TAB_NAMES = ['lint-triage', 'test-failures', 'playwright-tests', 'crash-test'];

// Component that throws an error when shouldCrash is true
const CrashComponent: React.FC<{ shouldCrash: boolean }> = ({ shouldCrash }) => {
  if (shouldCrash) {
    throw new Error('Test crash triggered from DevTools - ErrorBoundary test');
  }
  return null;
};

// Tab for testing the ErrorBoundary
const CrashTest: React.FC = () => {
  const [shouldCrash, setShouldCrash] = useState(false);

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        ErrorBoundary Crash Test
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Click the button below to trigger a React render error. This will test the ErrorBoundary
        and send an error report email to support@airplx.com.
      </Typography>
      <Button
        variant="contained"
        color="error"
        onClick={() => setShouldCrash(true)}
        data-testid="crash-test-button"
      >
        Trigger WSOD Error
      </Button>
      <CrashComponent shouldCrash={shouldCrash} />
    </Box>
  );
};

export const DevTools: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  // Get initial tab from URL hash parameter
  const getInitialTab = () => {
    const hash = location.hash;
    const params = new URLSearchParams(hash.split('?')[1] || '');
    const tabParam = params.get('tab');
    const tabIndex = TAB_NAMES.indexOf(tabParam || '');
    return tabIndex >= 0 ? tabIndex : 0;
  };

  const [tab, setTab] = useState(getInitialTab);

  // Update tab when URL changes
  useEffect(() => {
    const newTab = getInitialTab();
    if (newTab !== tab) {
      setTab(newTab);
    }
  }, [location.hash]);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTab(newValue);
    // Update URL hash with tab parameter
    const hash = location.hash.split('?')[0];
    navigate(`${hash}?tab=${TAB_NAMES[newValue]}`);
  };

  return (
    <Box>
      <Tabs value={tab} onChange={handleTabChange}>
        <Tab label={t('screens.devTools.tabs.lintTriage')} />
        <Tab label={t('screens.devTools.tabs.testFailures')} />
        <Tab label={t('screens.devTools.tabs.playwrightTests')} />
        <Tab label="Crash Test" />
      </Tabs>
      <Box sx={{ mt: 2 }}>
        {tab === 0 && <LintTriage />}
        {tab === 1 && <TestFailures />}
        {tab === 2 && <PlaywrightTests />}
        {tab === 3 && <CrashTest />}
      </Box>
    </Box>
  );
};
